import React from 'react';
import Carousel from '../components/Carousel';
import MyReviewsSection from '../components/MyReviewsSection';
import { Link } from 'react-router-dom';
import SEO from '../components/SEO';

const Home = () => {
  const stats = [
    { number: '5K+', label: 'Happy Customers' },
    { number: '6+', label: 'Years Experience' },
    { number: '7.1K+', label: 'Carpets Cleaned' },
    { number: '24/7', label: 'Support Available' }
  ];

  const features = [
    {
      icon: '🌿',
      title: 'Eco-Friendly Solutions',
      description: 'We use only environmentally safe cleaning products that are tough on stains but gentle on the planet and your family.',
      gradient: 'from-green-500 to-emerald-600'
    },
    {
      icon: '⚡',
      title: 'Quick & Efficient',
      description: 'Fast drying technology and efficient processes minimize disruption to your daily routine.',
      gradient: 'from-blue-500 to-cyan-600'
    },
    {
      icon: '💰',
      title: 'Best Value',
      description: 'Premium quality service at competitive prices with no hidden fees or surprise charges.',
      gradient: 'from-purple-500 to-pink-600'
    }
  ];

  const testimonials = [
    {
      name: 'Sarah Johnson',
      location: 'Denver, CO',
      text: 'GreenRocky transformed our old carpets! They look brand new and the eco-friendly smell was amazing.',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1494790108755-2616b612b786?ixlib=rb-4.0.3&auto=format&fit=crop&w=100&q=80'
    },
    {
      name: 'Mike Chen',
      location: 'Boulder, CO',
      text: 'Professional, punctual, and incredible results. Will definitely use their services again!',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1472099645785-5658abf4ff4e?ixlib=rb-4.0.3&auto=format&fit=crop&w=100&q=80'
    },
    {
      name: 'Emily Rodriguez',
      location: 'Fort Collins, CO',
      text: 'The stain removal was magical! Our white carpets are white again. Highly recommended!',
      rating: 5,
      image: 'https://images.unsplash.com/photo-1438761681033-6461ffad8d80?ixlib=rb-4.0.3&auto=format&fit=crop&w=100&q=80'
    }
  ];

  const processSteps = [
    {
      step: '01',
      title: 'Free Consultation',
      description: 'We assess your needs and provide a detailed quote'
    },
    {
      step: '02',
      title: 'Schedule Service',
      description: 'Choose a convenient time that works for you'
    },
    {
      step: '03',
      title: 'Professional Cleaning',
      description: 'Our certified technicians perform the service'
    },
    {
      step: '04',
      title: 'Quality Check',
      description: 'We ensure complete satisfaction before leaving'
    }
  ];

  return (
    <>
      <SEO 
        title="Professional Carpet Cleaning Services | GreenRocky Carpet"
        description="Get professional, eco-friendly carpet cleaning services for your home or business. Stain removal, deep cleaning, and emergency services available. Free estimates!"
        keywords="carpet cleaning, professional cleaners, stain removal, eco-friendly cleaning, deep cleaning, carpet maintenance"
        url="https://greenrockycarpet.com.au"
      />
    <div>
      <Carousel />
      
      {/* Stats Section */}
      <section className="stats-section">
        <div className="container">
          <div className="stats-grid">
            {stats.map((stat, index) => (
              <div key={index} className="stat-card">
                <div className="stat-number">{stat.number}</div>
                <div className="stat-label">{stat.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section className="features-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">Why Choose GreenRocky?</h2>
            <p className="section-subtitle">
              Experience the difference with our premium carpet cleaning services
            </p>
          </div>
          <div className="features-grid">
            {features.map((feature, index) => (
              <div key={index} className="feature-card">
                <div className={`home-feature-icon gradient-${index + 1}`}>
                  <span>{feature.icon}</span>
                </div>
                <h3 className="feature-title">{feature.title}</h3>
                <p className="feature-description">{feature.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Process Section */}
      <section className="process-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">How It Works</h2>
            <p className="section-subtitle">
              Simple, transparent process from start to finish
            </p>
          </div>
          <div className="process-steps">
            {processSteps.map((step, index) => (
              <div key={index} className="process-step">
                <div className="step-number">{step.step}</div>
                <div className="step-content">
                  <h3 className="step-title">{step.title}</h3>
                  <p className="step-description">{step.description}</p>
                </div>
                {index < processSteps.length - 1 && (
                  <div className="step-connector"></div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <section className="testimonials-section">
        <div className="container">
          <div className="section-header">
            <h2 className="section-title">What Our Customers Say</h2>
            <p className="section-subtitle">
              Don't just take our word for it - hear from our satisfied customers
            </p>
          </div>
          <div className="testimonials-grid">
            <MyReviewsSection />
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="cta-section">
        <div className="container">
          <div className="cta-content">
            <h2 className="cta-title">Ready for Spotless Carpets?</h2>
            <p className="cta-description">
              Join thousands of satisfied customers who trust GreenRocky for their carpet cleaning needs. 
              Book your service today and experience the premium difference.
            </p>
            <div className="cta-buttons">
              <Link to="/booking" className="cta-btn primary">
                Book Your Cleaning Now
              </Link>
              <Link to="/services" className="cta-btn secondary">
                View Our Services
              </Link>
            </div>
            <div className="cta-features">
              <div className="cta-feature">
                <span className="check-icon">✓</span>
                Free Estimates
              </div>
              <div className="cta-feature">
                <span className="check-icon">✓</span>
                Same Day Service Available
              </div>
              <div className="cta-feature">
                <span className="check-icon">✓</span>
                100% Satisfaction Guarantee
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Trust Badges */}
      <section className="trust-section">
        <div className="container">
          <div className="trust-content">
            <h3 className="trust-title">Trusted & Certified</h3>
            <div className="trust-badges">
              <div className="trust-badge">
                <div className="badge-icon">🏆</div>
                <span>IICRC Certified</span>
              </div>
              <div className="trust-badge">
                <div className="badge-icon">🌎</div>
                <span>Eco-Friendly</span>
              </div>
              <div className="trust-badge">
                <div className="badge-icon">⭐</div>
                <span>5-Star Rated</span>
              </div>
              <div className="trust-badge">
                <div className="badge-icon">🛡️</div>
                <span>Fully Insured</span>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  </>
  );
};

export default Home;